// src/ThreeJs/Global/UndoManager.js
// Very small global undo stack that stores closures.
//
// Each action is an object:
//   { label?: string, undo: () => void, redo?: () => void }

const MAX_UNDO = 10;
const undoStack = [];
const redoStack = [];
const UNDO_STACK_CHANGED_EVENT = "UNDO_STACK_CHANGED";

// Global flag: while false, pushUndoAction is ignored.
// This lets us hydrate existing segments without creating undo entries.
let undoRecordingEnabled = true;

function emitUndoStackChanged() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent(UNDO_STACK_CHANGED_EVENT, {
      detail: {
        canUndo: undoStack.length > 0,
        canRedo: redoStack.length > 0,
      },
    })
  );
}

/**
 * Enable recording of new undo actions.
 * Call this once the project has fully loaded and you only
 * want *user* actions in this session to be undoable.
 */
export function enableUndoRecording() {
  undoRecordingEnabled = true;
}

/**
 * Disable recording of new undo actions.
 * Use this while rebuilding polygons from saved data so that
 * those actions are *not* added to the undo history.
 */
export function disableUndoRecording() {
  undoRecordingEnabled = false;
}

/**
 * Push a new undoable *user* action on the stack.
 * This INVALIDATES the redo stack.
 *
 * @param {{ label?: string, undo: Function, redo?: Function }} action
 */
export function pushUndoAction(action) {
  // 🔒 Respect recording flag — do nothing while disabled
  if (!undoRecordingEnabled) return;

  if (!action || typeof action.undo !== "function") return;

  // Any *new* user action invalidates the redo history
  redoStack.length = 0;

  undoStack.push(action);

  // Keep only last MAX_UNDO actions
  if (undoStack.length > MAX_UNDO) {
    // drop the oldest action
    undoStack.shift();
  }
  emitUndoStackChanged();
}

/**
 * Internal helper: push an action onto undo stack *without* clearing redo.
 * Used from REDO so multi-step redo keeps working.
 *
 * @param {{ label?: string, undo: Function, redo?: Function }} action
 */
export function pushUndoActionFromRedo(action) {
  if (!action || typeof action.undo !== "function") return;

  undoStack.push(action);

  if (undoStack.length > MAX_UNDO) {
    undoStack.shift();
  }
  emitUndoStackChanged();
}

/**
 * Pop the last undo action without running it.
 */
export function popUndoAction() {
  if (!undoStack.length) return null;
  return undoStack.pop();
}

/**
 * Returns true if there is something to undo.
 */
export function canUndo() {
  return undoStack.length > 0;
}

/**
 * Push a redoable action on the redo stack.
 * Typically called after an undo.
 * @param {{ label?: string, undo: Function, redo?: Function }} action
 */
export function pushRedoAction(action) {
  if (!action) return;
  redoStack.push(action);

  // Keep redo history bounded as well
  if (redoStack.length > MAX_UNDO) {
    redoStack.shift();
  }
  emitUndoStackChanged();
}

/**
 * Pop the last redo action without running it.
 */
export function popRedoAction() {
  if (!redoStack.length) return null;
  return redoStack.pop();
}

/**
 * Returns true if there is something to redo.
 */
export function canRedo() {
  return redoStack.length > 0;
}

/**
 * Clear all stored undo/redo actions.
 */
export function clearUndoStack() {
  undoStack.length = 0;
  redoStack.length = 0;
  emitUndoStackChanged();
}

/**
 * Convenience helper for actually performing the last undo.
 * Safe to call even if there is nothing to undo.
 *
 * Note: this also feeds the undone action into the redo stack.
 */
export function UndoLastAction() {
  const action = popUndoAction();
  if (action && typeof action.undo === "function") {
    action.undo();
    pushRedoAction(action);
    emitUndoStackChanged();
    return;
  }
  emitUndoStackChanged();
}
